import { makePersisted, storageSync } from "@solid-primitives/storage";

import { createEffect, createRoot, createSignal, type JSX, onCleanup, onMount } from "solid-js";
import { client } from "~/js/client";
import { lzwEncode } from "~/js/encode";
import { getItem, giveItemsWorth, ItemCard } from "~/js/items";
import { clearProfilePicture, displayPopup, updateProfilePicture } from "~/js/online/profile";
import { prefCurrency, setPrefCurrency } from "~/js/prefs";
import { createPage } from "~/js/utils";
import { overwriteSave, wipeSave } from "~/script";
import type { ItemName } from "~/types";
import { user } from "~/userStore";

const GRAPHICS_LABELS: Record<GraphicsLevel, string> = {
	0: "Lowest & Less animations",
	1: "Low",
	2: "Normal",
	3: "Ultra",
};

createPage(() => settingsPage(), "about");

function normaliseGraphics(level: unknown): GraphicsLevel {
	const n = Number(level);
	if (!Number.isFinite(n)) return 1;
	return Math.min(3, Math.max(0, Math.trunc(n))) as GraphicsLevel;
}

export const [alwaysCompact, setAlwaysCompact] = makePersisted(createSignal(false), {
	name: "compactMode",
	serialize: (v) => String(v),
	deserialize: (v) => v === "true",
	sync: storageSync,
});

async function verifyTurnstile(token: string) {
	const res = await client.api.keyverify.$post({ json: { token } });
	if (res.ok) {
		return await res.json();
	}
	throw Error("Failed to verify turnstile token");
}

const [graphicsLevel, setGraphicsLevel] = createRoot(() => {
	const [graphicsLevel, setGraphicsLevel] = makePersisted(createSignal<GraphicsLevel>(2), {
		name: "graphicsLevel",
		serialize: (v) => String(v),
		deserialize: (v) => normaliseGraphics(v),
	});
	createEffect(() => {
		document.body.classList.toggle("graphics-level-0", graphicsLevel() >= 0);
		document.body.classList.toggle("graphics-level-1", graphicsLevel() >= 1);
		document.body.classList.toggle("graphics-level-2", graphicsLevel() >= 2);
		document.body.classList.toggle("graphics-level-3", graphicsLevel() >= 3);
		window.graphicsLevel = graphicsLevel();
	});

	return [graphicsLevel, setGraphicsLevel];
});

function settingsPage() {
	let turnstileContainer: HTMLDivElement;
	const [codeMessage, setCodeMessage] = createSignal<string | null>(null);
	const [saveSize, setSaveSize] = createSignal<number>(new Blob([localStorage.localsave]).size);
	const [rawSaveSize, setRawSaveSize] = createSignal<number>(new Blob([JSON.stringify(user)]).size);
	const [compact, setCompact] = createSignal(false);

	const [turnstileToken, setTurnstileToken] = createSignal<string | null>(null);

	const [caseSounds, setCaseSounds] = makePersisted(createSignal(false), {
		name: "case_prefs_sounds",
		serialize: (v) => (v ? "on" : "off"),
		deserialize: (v) => v === "on",
	});
	function onResize() {
		const natural = window.innerHeight <= 850 || window.innerWidth <= 1150;
		if (natural) {
			setCompact(true);
		} else {
			setCompact(false);
		}
		document.body.classList.toggle("compact", natural || alwaysCompact());
		document.body.classList.toggle("natural-compact", natural);
	}
	let intervalId: number;
	onMount(() => {
		intervalId = setInterval(() => {
			setSaveSize(new Blob([localStorage.localsave]).size);
			setRawSaveSize(new Blob([JSON.stringify(user)]).size);
		}, 2000);

		window.addEventListener("resize", onResize);
		console.log("turnstile key:", import.meta.env.VITE_TURNSTILE_SITE_KEY);
		turnstile.render(turnstileContainer, {
			sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
			"expired-callback"() {
				setTurnstileToken(null);
			},

			callback(token: string) {
				setTurnstileToken(token);
			},
		});
		onResize();
	});
	onCleanup(() => {
		turnstile.remove(turnstileContainer);
		clearInterval(intervalId);
		window.removeEventListener("resize", onResize);
	});
	const onSubmit: JSX.EventHandler<HTMLFormElement, SubmitEvent> = async (event) => {
		event.preventDefault();
		event.stopPropagation();
		try {
			const form = event.currentTarget;
			const codeInput = new FormData(form).get("code") as string;

			const res = await client.api.redeemgift[":code"].$get({ param: { code: codeInput } });
			if (!res.ok) {
				const data = await res.json();
				if ("description" in data) {
					setCodeMessage(data.description);
				}
				return;
			}
			const data = await res.json();

			if (data.type === "MONEY") user.money += data.value;
			if (data.type === "XP") user.xp += data.value;
			if (data.type === "WORTH") giveItemsWorth(data.value);
			setCodeMessage("The code worked!");
		} catch {
			setCodeMessage("An error occurred while redeeming the code.");
		}
	};
	return (
		<>
			<h1>Case Clicker 2 Settings</h1>
			<div id="about-settings">
				<div class="floating-item-example">
					<i>Item Card Example</i>
					<div id="floating-item-example">
						<ItemCard item={getItem("StatTrak USP-S | Neo-Noir (Factory New)" as ItemName)}></ItemCard>
					</div>
				</div>
				<h2>Settings</h2>
				<br />
				<p>
					<b>
						Graphics Level: <span>{GRAPHICS_LABELS[graphicsLevel()]}</span>
					</b>
				</p>
				<div style="display: flex;">
					<button class="combo-button" onclick={() => setGraphicsLevel(0)} type="button">
						Lowest (& less animations)
					</button>
					<button class="combo-button" onclick={() => setGraphicsLevel(1)} type="button">
						Low
					</button>
					<button class="combo-button" onclick={() => setGraphicsLevel(2)} type="button">
						Normal
					</button>
					<button class="combo-button" onclick={() => setGraphicsLevel(3)} type="button">
						<i>Ultra</i>
					</button>
				</div>
				<br />
				<p>
					<b>Currency</b>
				</p>
				<select
					onInput={(e) => {
						setPrefCurrency(e.target.value);
					}}
				>
					<option selected={prefCurrency() === "euro"} value="euro">
						Euro: €
					</option>
					<option selected={prefCurrency() === "dollar"} value="dollar">
						Dollar: $
					</option>
				</select>
				<p class="hidden">Please reload to update the currency everywhere.</p>
				<br />
				<br />
				<p>
					<b>Always Compact?</b>
				</p>
				<button class="button n" onClick={() => setAlwaysCompact((prev) => !prev)} type="button">
					{compact() ? "Compact is enabled due to window size" : "Toggle Compact"}
				</button>
				<br />
				<br />
				<p>
					<b>Enable Sounds?</b>
				</p>
				<button class="button n" onClick={() => setCaseSounds((prev) => !prev)} type="button">
					Sounds: {caseSounds() ? "On" : "Off"}
				</button>
				<br />
				<br />
				<p>
					<b>Enter Code</b>
				</p>
				<form onSubmit={onSubmit}>
					<input class="input" name="code" style="margin: 0px;" type="text" />
					<button class="button" type="submit">
						Enter
					</button>
				</form>
				{codeMessage() && <div>{codeMessage()}</div>}
				<br />
				<p>
					<b>Save</b>
				</p>
				<p>
					Compressed save size:{" "}
					<span>
						~{(saveSize() / 1000).toFixed(2)} kB ({(((saveSize() - rawSaveSize()) / rawSaveSize() / 1000) * 100).toFixed(1)}%)
					</span>
				</p>
				<p style="color: rgb(153, 153, 153); font-size: 13px; margin-bottom: 6px;">
					Sell items to reduce the save size, and to improve performance.
				</p>
				<button
					class="button n"
					onClick={() => {
						const blob = new Blob([lzwEncode(JSON.stringify(user))]);
						const url = URL.createObjectURL(blob);
						const a = document.createElement("a");
						a.href = url;
						a.download = "caseclicker.save";
						a.click();

						URL.revokeObjectURL(url);
						a.remove();
					}}
					type="button"
				>
					Backup or transfer your save
				</button>
				<button
					class="button n red"
					onClick={() => {
						const input = document.createElement("input");
						input.type = "file";
						input.accept = ".save";
						input.addEventListener("input", async () => {
							const file = input.files?.[0];
							if (file) {
								const text = await file.text();
								if (confirm("Are you sure, you want to load this save? This will overwrite the current save and cannot be undone.")) {
									overwriteSave(text);
								}
							}
						});
						input.click();
						input.remove();
					}}
					type="button"
				>
					Load save
				</button>
				<button
					class="button n red"
					onClick={() => {
						if (confirm("Are you sure? This cannot be undone.")) {
							wipeSave();
						}
					}}
					type="button"
				>
					Wipe your save
				</button>
				<br />
				<br />
				<p>
					<b>Profile</b>
				</p>
				<button class="button n" onClick={() => displayPopup(true)} type="button">
					Change name
				</button>
				<button class="button n" onClick={updateProfilePicture} type="button">
					Update profile picture
				</button>
				<button class="button n" onClick={clearProfilePicture} type="button">
					Remove profile picture
				</button>
				<p style="color: rgb(153, 153, 153); font-size: 13px;">
					Profile pictures are currently stored locally, and aren't yet used for online features. They may be uploaded and used for online
					features in the future.
				</p>
				<p>
					<b>Online</b>
				</p>
				<button
					class="button"
					disabled={turnstileToken() === null}
					onClick={async () => {
						// only allow reset every minute

						user.online.key = null;
						// void promptOnlineKey();
						const data = await verifyTurnstile(turnstileToken()!);
						if (data) {
							user.online.key = data.id;
							user.online.keyCreated = Date.now();
						} else {
							alert("fuckkkkkkkkkkkkkkkkkk");
						}
					}}
					type="button"
				>
					Reset Online Key
				</button>
				<div
					id="turnstile-container"
					ref={(el) => {
						turnstileContainer = el;
					}}
				/>
			</div>
			<div id="about-changelog">
				<h2>Changelog</h2>
				<br />
				<a href="/changelog.txt" rel="noopener" target="_blank">
					<button class="button n" type="button">
						See changelog
					</button>
				</a>
			</div>
			<div id="about-credits">
				<p>
					Created By <a href="https://x.com/ozenikien">ozenikien</a>
				</p>
				<br />
				<p>
					Some icons are from <a href="//icons8.com">icons8.com</a>
				</p>
				<br />
				<p>Thanks to Malthe for creating the original Case Clicker game, and allowing me to fork his code.</p>
				<br />
				<p>The images used for skins and items are property of Valve Corp, and provided via the Steam Marketplace.</p>
				<p>This site is not associated with Valve Corp.</p>
			</div>
		</>
	);
}
